import { ChangeEvent, useEffect, useRef } from 'react';
import { IChatEntity } from '@/shared/interfaces/chat-entity.interface';
import { ChatEntity } from '@/components/ChatEntity/ChatEntity';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button, DatePicker, Dropdown, Typography } from 'antd';
import { Sorting } from '@/components/Sorting/Sorting';
import sortIcon from '@/public/icons/sort.svg';
import emptyIcon from '@/public/icons/empty.svg';
import { observer } from 'mobx-react-lite';
import { DirectionChatType } from '@/shared/interfaces/inbox-filter.interface';
import { useStore } from '@/stores';
import InputSearch from '@/components/InputSearch/InputSearch';
import { FilterIcon } from '@/public/icons/filters/FilterIcon';
import { State } from '@/services/state.service';
import './Inbox.scss';

const { RangePicker } = DatePicker;
const { Title } = Typography;

export const Inbox = observer(() => {
  const {
    chatsStore: { inboxChats, setInboxFilter, inboxFilter },
  } = useStore();

  const { searchTerm, orderBy, dateRange } = inboxFilter;

  const prevElItem = useRef<HTMLDivElement>(null);
  const datePicker = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  const openChatDetails = async (item: IChatEntity) => {
    State.setState({ prevInboxChatId: item.chatId });
    navigate(`/inbox/${item.chatId}`, { state: { from: '/inbox' } });
  };

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    const value = e.target.value;
    setInboxFilter({ searchTerm: value });
  };

  const onDateSortChange = (values: any) => {
    const newFromDate = values[0].set({ hour: 0o0, minute: 0o0, second: 0o1 });
    const newToDate = values[1].set({ hour: 23, minute: 59, second: 59 });
    setInboxFilter({
      dateRange: {
        fromDate: newFromDate,
        toDate: newToDate,
      },
    });
  };

  const onSortingApplied = (value: DirectionChatType) => {
    setInboxFilter({
      orderBy: {
        field: 'createdTimestamp',
        direction: value,
      },
    });
  };

  useEffect(() => {
    const { prevInboxChatId } = State.state$.getValue();
    if (prevInboxChatId && prevElItem?.current) {
      prevElItem.current.scrollIntoView({ block: 'center', inline: 'nearest' });
    }
  }, [location.state]);

  const closeDatePickerBtn = () => {
    return (
      <Button
        data-testid="close-date-picker"
        onClick={() => datePicker.current.blur()}
        size="small"
        type="primary"
        ghost
      >
        Close
      </Button>
    );
  };

  const renderItems = () => {
    if (!inboxChats?.length) {
      return (
        <div className="empty-box-container">
          <img src={emptyIcon} alt="empty" />
          <div>There are no any channels</div>
        </div>
      );
    }

    return inboxChats.map((item, i) => {
      const { prevInboxChatId } = State.state$.getValue();
      const isPrevious = item.chatId === prevInboxChatId;

      return (
        <div
          ref={isPrevious ? prevElItem : null}
          onClick={() => openChatDetails(item)}
          className={'user-inbox-item'}
          key={`i_${i}${item.chatId}`}
        >
          <ChatEntity chatEntity={item} isPrevious={isPrevious}></ChatEntity>
        </div>
      );
    });
  };

  return (
    <div className={'inbox-page'}>
      <div className="heading">
        <Title level={5}>
          Inbox {inboxChats?.length > 0 ? `(${inboxChats.length})` : ''}
        </Title>

        <span>
          <Dropdown
            overlay={() => (
              <Sorting
                setSortingApplied={onSortingApplied}
                sortingApplied={orderBy.direction}
              />
            )}
            placement="bottom"
            arrow
          >
            <button className="heading-button">
              <img src={sortIcon} alt="sort" />
            </button>
          </Dropdown>
        </span>
      </div>
      <div>
        <button className="heading-button">
          <RangePicker
            ref={datePicker}
            value={[dateRange.fromDate, dateRange.toDate]}
            suffixIcon={<FilterIcon />}
            bordered={false}
            className="range-picker"
            renderExtraFooter={closeDatePickerBtn}
            onChange={onDateSortChange}
          />
        </button>
      </div>
      <div>
        <div className="search-container">
          <InputSearch
            onChange={onChange}
            data-testid="inbox-input-search"
            placeholder="Search channel"
            value={searchTerm}
          />
        </div>
      </div>
      <div className="inbox-content">{renderItems()}</div>
    </div>
  );
});
